var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useState, useMemo, useCallback } from "react";
import { Accordion, AccordionSet, Button, Col, Loading, MessageBanner, Modal, ModalFooter, Row, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useOkapiKy } from "@folio/stripes/core";
import { FieldRow } from "../FieldRow";
import { AccordionSection } from "../AccordionSection";
import { ExpandCollapseToggle } from "../ExpandCollapseToggle";
import { RecordHistory } from "../enforcement/RecordHistory";
import { DepositRecordStatusDialog } from "../enforcement/DepositRecordStatusDialog";
import { LibraryReference } from "../library/LibraryReference";
import { LibraryNoteRow } from "./LibraryNoteRow";
import { ProvisionalBadge } from "../common/ProvisionalBadge";
import { VariantTitlesList } from "../common/VariantTitlesList";
import { PublisherLink } from "../publisher/PublisherLink";
import { DuplicateTitlesBanner } from "./merge/DuplicateTitlesBanner";
import { MergedRecordsSection } from "./merge/MergedRecordsSection";
import { AgencyNotificationsSection } from "./AgencyNotificationsSection";
import { AgendaBadges } from "../agenda/AgendaBadges";
import { MonographUnitLine } from "../agenda/MonographUnitLine";
import { STATUS_LABEL_IDS } from "../../types/enforcement";
import { fetchTitleById } from "../../api/titleService";
import { formatDate } from "../../utils/formatUtils";
import { VOLUME_STATUS } from "../../utils/volumeConstants";
import { formatIsbn } from "../../utils/isbn";
import { VolumeStatusBadge } from "../common/VolumeStatusBadge";
import styles from "./TitleDetailPane.module.css";
var canReceive = function (status) {
    return status === "CREATED" ||
        status === "OVERDUE" ||
        status === "DAMAGED" ||
        status === "DAMAGED_REPORTED" ||
        status === "REQUESTED";
};
var canReportDamaged = function (status) {
    return status !== "EXCLUDED" &&
        status !== "DAMAGED" &&
        status !== "DAMAGED_REPORTED";
};
export var TitleDetailPane = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j;
    var title = _a.title, subtitle = _a.subtitle, depositRecords = _a.depositRecords, isLoading = _a.isLoading, expandedRecordIds = _a.expandedRecordIds, onToggleRecord = _a.onToggleRecord, onReceive = _a.onReceive, onReportDamaged = _a.onReportDamaged, onStatusChanged = _a.onStatusChanged, onOpenMerge = _a.onOpenMerge, titleId = _a.titleId, titleInfo = _a.titleInfo, issueRow = _a.issueRow, agendaRow = _a.agendaRow;
    var formatMessage = useIntl().formatMessage;
    var ky = useOkapiKy();
    var titleDetailQuery = useQuery(["title", titleId], function () { return fetchTitleById(ky, titleId); }, { enabled: !!titleId });
    var libraryNote = (_c = (_b = titleDetailQuery.data) === null || _b === void 0 ? void 0 : _b.libraryNote) !== null && _c !== void 0 ? _c : null;
    var badgeRow = issueRow !== null && issueRow !== void 0 ? issueRow : agendaRow;
    var _k = useState(function () {
        var s = { basicInfo: true };
        depositRecords.forEach(function (r) {
            s["volume-".concat(r.id)] = false;
        });
        return s;
    }), accordionStatus = _k[0], setAccordionStatus = _k[1];
    var expandAll = Object.values(accordionStatus).every(Boolean);
    var _l = useState(null), confirmReceiveId = _l[0], setConfirmReceiveId = _l[1];
    var _m = useState(null), confirmDamagedId = _m[0], setConfirmDamagedId = _m[1];
    var _o = useState(null), statusDialogRecordId = _o[0], setStatusDialogRecordId = _o[1];
    var statusDialogRecord = (_d = depositRecords.find(function (record) { return record.id === statusDialogRecordId; })) !== null && _d !== void 0 ? _d : null;
    var toggleAccordion = useCallback(function (id) {
        setAccordionStatus(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[id] = !prev[id], _a)));
        });
    }, []);
    var toggleExpandCollapseAll = useCallback(function () {
        var newState = {};
        Object.keys(accordionStatus).forEach(function (key) {
            newState[key] = !expandAll;
        });
        setAccordionStatus(newState);
    }, [accordionStatus, expandAll]);
    var receivedCount = useMemo(function () {
        return depositRecords.filter(function (r) {
            return r.status === "RECEIVED" || r.status === "RECEIVED_AFTER_ENFORCEMENT";
        }).length;
    }, [depositRecords]);
    var totalCount = depositRecords.length;
    var overallStatus = useMemo(function () {
        if (totalCount === 0)
            return "CREATED";
        if (receivedCount === totalCount)
            return "RECEIVED";
        if (depositRecords.some(function (r) { return r.status === "OVERDUE"; }))
            return "OVERDUE";
        return "CREATED";
    }, [depositRecords, receivedCount, totalCount]);
    var sortedRecords = useMemo(function () {
        return __spreadArray([], depositRecords, true).sort(function (a, b) {
            var _a, _b;
            if (a.type !== b.type)
                return a.type === "MANDATORY" ? -1 : 1;
            return ((_a = a.copyNumber) !== null && _a !== void 0 ? _a : 0) - ((_b = b.copyNumber) !== null && _b !== void 0 ? _b : 0);
        });
    }, [depositRecords]);
    var firstExpectedDate = (_e = depositRecords[0]) === null || _e === void 0 ? void 0 : _e.expectedDeliveryDate;
    if (isLoading) {
        return React.createElement(Loading, null);
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("h1", null,
            title,
            ((_f = titleDetailQuery.data) === null || _f === void 0 ? void 0 : _f.provisional) && React.createElement(ProvisionalBadge, null),
            badgeRow && (React.createElement(AgendaBadges, { agenda: badgeRow.agenda, globalType: badgeRow.globalType, link: badgeRow.specialIssueLink, linkSide: issueRow ? "issue" : "monograph" }))),
        issueRow && React.createElement(MonographUnitLine, { row: issueRow }),
        subtitle && React.createElement("p", { className: styles.subtitle }, subtitle),
        issueRow && (React.createElement(MessageBanner, { type: "default", "data-test-id": "issue-row-limits" },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.issueRow.limitedActions" }))),
        onOpenMerge && (React.createElement(DuplicateTitlesBanner, { titleId: titleId, onOpenMerge: onOpenMerge })),
        React.createElement(ExpandCollapseToggle, { expanded: expandAll, onToggle: toggleExpandCollapseAll }),
        React.createElement(AccordionSet, null,
            React.createElement(AccordionSection, { id: "basicInfo", labelId: "ui-br-legaldeposits.detail.section.basicInfo", open: (_g = accordionStatus.basicInfo) !== null && _g !== void 0 ? _g : true, onToggle: function () { return toggleAccordion("basicInfo"); } },
                React.createElement(Row, null,
                    React.createElement(Col, { xs: 12, md: 6 },
                        React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.detail.field.title", value: title })),
                    React.createElement(Col, { xs: 12, md: 6 },
                        React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.detail.field.author", value: titleInfo === null || titleInfo === void 0 ? void 0 : titleInfo.author }))),
                React.createElement(VariantTitlesList, { variants: titleInfo === null || titleInfo === void 0 ? void 0 : titleInfo.variantTitles }),
                React.createElement(Row, null,
                    React.createElement(Col, { xs: 12, md: 6 },
                        React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.detail.field.isbn", value: formatIsbn(titleInfo === null || titleInfo === void 0 ? void 0 : titleInfo.isbn) })),
                    React.createElement(Col, { xs: 12, md: 6 },
                        React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.detail.field.publisher", value: titleDetailQuery.isSuccess && (titleInfo === null || titleInfo === void 0 ? void 0 : titleInfo.publisher) ? (React.createElement(PublisherLink, { name: titleInfo.publisher, publisherId: (_h = titleDetailQuery.data) === null || _h === void 0 ? void 0 : _h.ownerId, tooltipId: "title-detail-publisher-".concat(titleId) })) : (titleInfo === null || titleInfo === void 0 ? void 0 : titleInfo.publisher) }))),
                React.createElement(Row, null,
                    React.createElement(Col, { xs: 12, md: 6 },
                        React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.detail.field.year", value: (_j = titleInfo === null || titleInfo === void 0 ? void 0 : titleInfo.publicationYear) === null || _j === void 0 ? void 0 : _j.toString() })),
                    React.createElement(Col, { xs: 12, md: 6 },
                        React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.detail.field.edition", value: titleInfo === null || titleInfo === void 0 ? void 0 : titleInfo.editionStatement }))),
                React.createElement(Row, null,
                    React.createElement(Col, { xs: 12, md: 6 },
                        React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.detail.field.binding", value: titleInfo === null || titleInfo === void 0 ? void 0 : titleInfo.bindingText }))),
                React.createElement(Row, null,
                    React.createElement(Col, { xs: 12, md: 6 },
                        React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.detail.field.status", value: React.createElement(FormattedMessage, { id: STATUS_LABEL_IDS[overallStatus] }) })),
                    React.createElement(Col, { xs: 12, md: 6 },
                        React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.detail.field.copies", value: formatMessage({ id: "ui-br-legaldeposits.detail.field.copies.value" }, { received: receivedCount, total: totalCount }) }))),
                React.createElement(Row, null,
                    React.createElement(Col, { xs: 12, md: 6 },
                        React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.detail.field.created", value: (titleInfo === null || titleInfo === void 0 ? void 0 : titleInfo.createdAt)
                                ? formatDate(titleInfo.createdAt)
                                : undefined })),
                    React.createElement(Col, { xs: 12, md: 6 },
                        React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.detail.field.deadline", value: firstExpectedDate ? formatDate(firstExpectedDate) : undefined }))),
                titleId && (React.createElement(Row, null,
                    React.createElement(Col, { xs: 12 },
                        React.createElement(LibraryNoteRow, { note: libraryNote }))))),
            !issueRow && React.createElement(MergedRecordsSection, { titleId: titleId }),
            !issueRow && React.createElement(AgencyNotificationsSection, { titleId: titleId }),
            (function () {
                var mandatoryIndex = 0;
                var requestedIndex = 0;
                return sortedRecords.map(function (record) {
                    var _a, _b;
                    var isRequested = record.type === "REQUESTED";
                    var number = isRequested ? ++requestedIndex : ++mandatoryIndex;
                    var volumeStatus = (_a = VOLUME_STATUS[record.status]) !== null && _a !== void 0 ? _a : "missing";
                    var accordionId = "volume-".concat(record.id);
                    var baseVolumeLabel = formatMessage({
                        id: isRequested
                            ? "ui-br-legaldeposits.detail.volume.label.copy"
                            : "ui-br-legaldeposits.detail.volume.label",
                    }, { number: number });
                    var volumeLabel = record.extraCopyReason
                        ? "".concat(baseVolumeLabel, " - ").concat(formatMessage({
                            id: "ui-br-legaldeposits.detail.volume.extraReason.".concat(record.extraCopyReason),
                        }))
                        : baseVolumeLabel;
                    return (React.createElement(Accordion, { key: record.id, id: accordionId, label: React.createElement(React.Fragment, null,
                            volumeLabel,
                            "  ",
                            React.createElement("span", { className: styles.volumeBadge },
                                React.createElement(VolumeStatusBadge, { status: volumeStatus }))), open: (_b = accordionStatus[accordionId]) !== null && _b !== void 0 ? _b : false, onToggle: function () { return toggleAccordion(accordionId); } },
                        React.createElement(Row, null,
                            React.createElement(Col, { xs: 12, md: 6 },
                                React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.detail.field.library", value: React.createElement(LibraryReference, { name: record.libraryName, status: record.libraryStatus }) })),
                            React.createElement(Col, { xs: 12, md: 6 },
                                React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.detail.field.status", value: React.createElement(FormattedMessage, { id: STATUS_LABEL_IDS[record.status] }) }))),
                        React.createElement(Row, null,
                            React.createElement(Col, { xs: 12, md: 6 },
                                React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.detail.field.deadline", value: record.expectedDeliveryDate
                                        ? formatDate(record.expectedDeliveryDate)
                                        : undefined })),
                            record.receivedDate && (React.createElement(Col, { xs: 12, md: 6 },
                                React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.detail.field.receivedDate", value: formatDate(record.receivedDate) })))),
                        record.extraCopyNote && (React.createElement(Row, null,
                            React.createElement(Col, { xs: 12 },
                                React.createElement(FieldRow, { labelId: "ui-br-legaldeposits.detail.field.extraCopyNote", value: record.extraCopyNote })))),
                        React.createElement(Row, null,
                            React.createElement(Col, { xs: 12 },
                                canReceive(record.status) && (React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, onClick: function () { return setConfirmReceiveId(record.id); } },
                                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.enforcement.action.receive" }))),
                                " ",
                                canReportDamaged(record.status) && (React.createElement(Button, { buttonStyle: "default", marginBottom0: true, onClick: function () { return setConfirmDamagedId(record.id); } },
                                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.enforcement.action.reportDamaged" }))),
                                " ",
                                record.status !== "EXCLUDED" && (React.createElement(Button, { buttonStyle: "default", marginBottom0: true, onClick: function () { return setStatusDialogRecordId(record.id); } },
                                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.enforcement.action.changeStatus" }))),
                                " ",
                                React.createElement(Button, { buttonStyle: "default", marginBottom0: true, onClick: function () { return onToggleRecord(record.id); } },
                                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.enforcement.action.history" })))),
                        expandedRecordIds.has(record.id) && (React.createElement(RecordHistory, { recordId: record.id }))));
                });
            })()),
        React.createElement(Modal, { open: confirmDamagedId !== null, label: formatMessage({
                id: "ui-br-legaldeposits.enforcement.action.confirmDamaged",
            }), onClose: function () { return setConfirmDamagedId(null); }, footer: React.createElement(ModalFooter, null,
                React.createElement(Button, { buttonStyle: "warning", onClick: function () {
                        onReportDamaged(confirmDamagedId);
                        setConfirmDamagedId(null);
                    } }, formatMessage({
                    id: "ui-br-legaldeposits.enforcement.action.confirmDamaged",
                })),
                React.createElement(Button, { onClick: function () { return setConfirmDamagedId(null); } }, formatMessage({ id: "ui-br-legaldeposits.button.cancel" }))) },
            React.createElement("p", null,
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.enforcement.action.confirmDamaged.message" }))),
        React.createElement(Modal, { open: confirmReceiveId !== null, label: formatMessage({
                id: "ui-br-legaldeposits.enforcement.action.confirmReceive",
            }), onClose: function () { return setConfirmReceiveId(null); }, footer: React.createElement(ModalFooter, null,
                React.createElement(Button, { buttonStyle: "primary", onClick: function () {
                        onReceive(confirmReceiveId);
                        setConfirmReceiveId(null);
                    } }, formatMessage({
                    id: "ui-br-legaldeposits.enforcement.action.confirmReceive",
                })),
                React.createElement(Button, { onClick: function () { return setConfirmReceiveId(null); } }, formatMessage({ id: "ui-br-legaldeposits.button.cancel" }))) },
            React.createElement("p", null,
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.enforcement.action.confirmReceive.message" }))),
        React.createElement(DepositRecordStatusDialog, { record: statusDialogRecord, onClose: function () { return setStatusDialogRecordId(null); }, onStatusChanged: onStatusChanged })));
};
